import { useEffect, useState } from "react";
import { PanelHeader } from "./PanelHeader";
import { fmtAge } from "@kmitl/shared";
import type { FallbackTier } from "@kmitl/shared";

/**
 * AdapterHealthPanel — live read of /api/health/adapters.
 *
 * Polls every 30 s. Renders a compact per-adapter row with:
 *   status dot (healthy / degraded / warming / failing)
 *   name + records-last
 *   last-success age
 *   p50 / p95 latency over the last hour
 *   1-h success rate
 *
 * Source of truth for the topbar "PIPE n/m" pill and for the
 * SourceCatalog modal's live-health block.
 */

export type AdapterStatus = "healthy" | "degraded" | "warming" | "failing";

export interface AdapterHealth {
  name: string;
  status: AdapterStatus;
  ok: boolean;
  lastSuccessAt: string | null;
  lastSuccessAgeMin: number | null;
  lastErrorAt: string | null;
  lastErrorMsg: string | null;
  latencyMsP50: number | null;
  latencyMsP95: number | null;
  successRate1h: number | null;
  successRate24h: number | null;
  fetchCount1h: number;
  fetchCount24h: number;
  recordsLast: number | null;
  cacheHitsFresh: number;
  cacheHitsStale: number;
  cacheMisses: number;
}

const STATUS_COLOR: Record<AdapterStatus, string> = {
  healthy:  "var(--good)",
  degraded: "var(--warn)",
  warming:  "var(--text-3)",
  failing:  "var(--bad)",
};

const STATUS_LABEL: Record<AdapterStatus, string> = {
  healthy:  "OK",
  degraded: "DEG",
  warming:  "WARM",
  failing:  "FAIL",
};

function fmtMs(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
function fmtPct(p: number | null): string {
  if (p == null) return "—";
  return `${Math.round(p * 100)}%`;
}

export function useAdapterHealth(apiBase: string, intervalMs = 30_000): AdapterHealth[] {
  const [adapters, setAdapters] = useState<AdapterHealth[]>([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${apiBase}/api/health/adapters`);
        if (!r.ok) return;
        const j = (await r.json()) as { adapters?: AdapterHealth[] };
        if (!cancelled && Array.isArray(j.adapters)) setAdapters(j.adapters);
      } catch { /* swallow */ }
    };
    load();
    const id = setInterval(load, intervalMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [apiBase, intervalMs]);
  return adapters;
}

interface Props {
  adapters: AdapterHealth[];
  fallbackTier?: FallbackTier;
  ageMinutes?: number;
}

export function AdapterHealthPanel({ adapters, fallbackTier, ageMinutes }: Props) {
  if (adapters.length === 0) {
    return <div className="adapter-health-empty caption">Pipeline metrics loading…</div>;
  }
  return (
    <section className="adapter-health">
      <header className="adapter-health-head">
        <span className="lcars-bar" />
        <PanelHeader
          title="PIPELINE"
          fallbackTier={fallbackTier ?? "live"}
          ageMinutes={ageMinutes}
          actions={<span className="mono caption">{adapters.length}</span>}
        />
      </header>
      <ul className="adapter-health-list">
        <li className="adapter-health-row adapter-health-head-row mono caption">
          <span />
          <span>SOURCE</span>
          <span className="ah-num">AGE</span>
          <span className="ah-num">p50</span>
          <span className="ah-num">p95</span>
          <span className="ah-num">sr1h</span>
          <span className="ah-num">recs</span>
        </li>
        {adapters.map((a) => (
          <li
            key={a.name}
            className="adapter-health-row"
            title={a.lastErrorMsg ? `last error: ${a.lastErrorMsg}` : undefined}
          >
            <span
              className="adapter-health-dot"
              style={{ background: STATUS_COLOR[a.status] }}
              aria-label={STATUS_LABEL[a.status]}
            />
            <span className="adapter-health-name mono">{a.name}</span>
            <span className="ah-num mono">{fmtAge(a.lastSuccessAgeMin)}</span>
            <span className="ah-num mono">{fmtMs(a.latencyMsP50)}</span>
            <span className="ah-num mono">{fmtMs(a.latencyMsP95)}</span>
            <span className="ah-num mono" style={{ color: a.successRate1h != null && a.successRate1h < 0.8 ? STATUS_COLOR.failing : undefined }}>
              {fmtPct(a.successRate1h)}
            </span>
            <span className="ah-num mono">{a.recordsLast ?? "—"}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Tiny topbar pill summarising live-feed health in plain language a
 * non-technical viewer can read at a glance. Hover for the engineer's
 * breakdown (per-status counts).
 *
 * Phrasing:
 *   • all healthy           → "All feeds healthy"
 *   • only warming          → "N feeds warming"
 *   • only degraded         → "N feeds slow"   / "1 feed slow"
 *   • only failing          → "N feeds offline"/ "1 feed offline"
 *   • mixed degraded+failing → "N slow · M offline"
 */
export function pipelineSummary(degraded: number, failing: number, warming: number, healthy: number): string {
  if (failing > 0 && degraded > 0) {
    return `${degraded} slow · ${failing} offline`;
  }
  if (failing > 0) {
    return failing === 1 ? "1 feed offline" : `${failing} feeds offline`;
  }
  if (degraded > 0) {
    return degraded === 1 ? "1 feed slow" : `${degraded} feeds slow`;
  }
  if (warming > 0 && healthy === 0) {
    return warming === 1 ? "1 feed warming" : `${warming} feeds warming`;
  }
  return "All feeds healthy";
}

export function PipelinePill({ adapters }: Props) {
  if (adapters.length === 0) return null;
  const total = adapters.length;
  const failing = adapters.filter((a) => a.status === "failing").length;
  const degraded = adapters.filter((a) => a.status === "degraded").length;
  const healthy = adapters.filter((a) => a.status === "healthy").length;
  const warming = total - failing - degraded - healthy;
  const color =
    failing > 0 ? STATUS_COLOR.failing :
    degraded > 0 ? STATUS_COLOR.degraded :
    STATUS_COLOR.healthy;
  const label = pipelineSummary(degraded, failing, warming, healthy);
  return (
    <span
      className="pipeline-pill mono"
      style={{ borderColor: color, color }}
      title={`Pipeline · ${healthy} healthy · ${degraded} degraded · ${failing} failing · ${warming} warming  (of ${total})`}
    >
      <span className="pipeline-pill-dot" style={{ background: color }} />
      {label}
    </span>
  );
}
